import React, { useState, useEffect } from 'react';
import { Users, ChevronDown, ChevronUp } from 'lucide-react';
import { Product } from '../../types/product';
import { Product as KnowledgeProduct } from '../../types/knowledge';
import productService from '../../services/api/productService';
import SegmentAppealPicker, { SegmentAppealSelection } from '../image-generation/SegmentAppealPicker';
import { TaskyButton } from '../tasky';
import type { SelectedProductImage } from '../../redux/chat/chatSlice';

/**
 * 商品と一緒に返す付帯情報。どちらも任意で、未選択・未登録なら null。
 * 送信〜生成で参照する配線は別途（このモーダルは選択結果を返すところまで）。
 */
export interface ChatProductSelectionDetail {
  segmentAppeal: SegmentAppealSelection | null;
  primaryImage: SelectedProductImage | null;
}

interface ProductSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  // 第2引数は後方互換のため任意。既存の呼び出し側は product だけ受け取ればよい。
  onSelect: (product: Product, detail?: ChatProductSelectionDetail) => void;
}

// 商品のメイン画像（is_primary 優先、無ければ先頭）。一覧 API は product_images を含む。
const pickPrimaryImage = (product: Product): SelectedProductImage | null => {
  const images = product.product_images && product.product_images.length > 0 ? product.product_images : product.images || [];
  const image = images.find((img) => img.is_primary) || images[0];
  return image?.url ? { id: image.id, url: image.url } : null;
};

const ProductSelectionModal: React.FC<ProductSelectionModalProps> = ({
  isOpen,
  onClose,
  onSelect,
}) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 画像生成側（image-generation/ProductSelectionModal）と同じく、セグメント／訴求は
  // 選ばない人もいるので既定で閉じたアコーディオンにし、開くたびに閉じた状態から始める。
  const [selectedIntent, setSelectedIntent] = useState<SegmentAppealSelection | null>(null);
  const [isIntentOpen, setIsIntentOpen] = useState(false);

  // Fetch products when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchProducts();
    } else {
      // Reset state when modal closes
      setSelectedProductId(null);
      setError(null);
      setSelectedIntent(null);
      setIsIntentOpen(false);
    }
  }, [isOpen]);

  // 商品を切り替えたら、前の商品のセグメント／訴求は引き継がない。
  useEffect(() => {
    setSelectedIntent(null);
    setIsIntentOpen(false);
  }, [selectedProductId]);

  const selectedProduct = products.find((p) => p.id === selectedProductId) || null;

  const fetchProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await productService.getProducts({
        status: 'active',
        per_page: 100,
      });
      setProducts(response.data);
    } catch (err: any) {
      console.error('Failed to fetch products:', err);
      setError(err?.message || '商品の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProduct = (productId: number) => {
    setSelectedProductId(productId);
  };

  const handleAddProduct = () => {
    if (!selectedProductId) return;

    if (!selectedProduct) return;

    onSelect(selectedProduct, {
      segmentAppeal: selectedIntent,
      primaryImage: pickPrimaryImage(selectedProduct),
    });
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleOverlayClick}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">商品を選択</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="閉じる"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="mt-4 text-gray-600">読み込み中...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600">{error}</p>
              <button
                onClick={fetchProducts}
                className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
              >
                再試行
              </button>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">
                商品が登録されていません。ナレッジ管理画面から商品を登録してください。
              </p>
            </div>
          ) : (
            <div className="space-y-2" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {products.map((product) => (
                <label
                  key={product.id}
                  className={`
                    flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all
                    ${
                      selectedProductId === product.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }
                  `}
                >
                  <input
                    type="radio"
                    name="product"
                    value={product.id}
                    checked={selectedProductId === product.id}
                    onChange={() => handleSelectProduct(product.id)}
                    className="mt-1 mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900">{product.name}</div>
                    {product.description && (
                      <div className="mt-1 text-sm text-gray-600 line-clamp-2">
                        {product.description}
                      </div>
                    )}
                  </div>
                </label>
              ))}
            </div>
          )}

          {/* Segment / Appeal Selection（任意）。商品を選んだときだけ、既定で閉じたアコーディオンで出す。 */}
          {selectedProduct && !loading && !error && (
            <div className="mt-4 overflow-hidden rounded-lg border border-gray-200">
              <TaskyButton
                type="button"
                variant="ghost"
                aria-expanded={isIntentOpen}
                className="flex h-auto w-full items-center justify-between gap-2 rounded-none bg-gray-50 px-4 py-3 text-left hover:bg-gray-100"
                onClick={() => setIsIntentOpen((open) => !open)}
              >
                <span className="flex min-w-0 items-center gap-1.5 text-sm font-medium text-gray-700">
                  <Users size={14} className="shrink-0 text-gray-500" />
                  <span className="shrink-0 whitespace-nowrap">誰に、何を伝えるか</span>
                  <span className="optional ml-1 shrink-0 whitespace-nowrap text-xs font-normal text-gray-400">任意</span>
                  {/* 閉じていても選んだ内容を見失わないよう、見出しに要約を出す。 */}
                  {selectedIntent && (
                    <span className="ml-1 min-w-0 truncate rounded bg-violet-100 px-1.5 py-0.5 text-xs font-medium text-violet-700">
                      {selectedIntent.segmentName} / {selectedIntent.appealName}
                    </span>
                  )}
                </span>
                {isIntentOpen ? (
                  <ChevronUp size={18} className="shrink-0 text-gray-500" />
                ) : (
                  <ChevronDown size={18} className="shrink-0 text-gray-500" />
                )}
              </TaskyButton>
              {isIntentOpen && (
                <div className="flex flex-col gap-1.5 p-3">
                  <SegmentAppealPicker
                    // Picker は types/knowledge の Product を受けるが、読むのは extended_metadata だけで構造上互換。
                    product={selectedProduct as unknown as KnowledgeProduct}
                    selectedAppealId={selectedIntent?.appealId ?? null}
                    onSelect={setSelectedIntent}
                    onClear={() => setSelectedIntent(null)}
                  />
                  {selectedIntent?.appealDirection && (
                    <div className="rounded bg-gray-50 px-2.5 py-1.5 text-xs leading-snug text-gray-500">
                      {selectedIntent.appealDirection}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
          >
            キャンセル
          </button>
          <button
            onClick={handleAddProduct}
            disabled={!selectedProductId || loading}
            className={`
              px-4 py-2 text-white rounded transition-colors
              ${
                selectedProductId && !loading
                  ? 'bg-blue-500 hover:bg-blue-600'
                  : 'bg-gray-300 cursor-not-allowed'
              }
            `}
          >
            追加
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductSelectionModal;
